import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Global cache state for sharing across components
let globalFolderMap = {};
let globalLastFetch = null;
let globalInvalidationCallbacks = [];

// Global cache invalidation function
export const invalidateFolderCache = () => {
  globalFolderMap = {};
  globalLastFetch = null;
  // Notify all active hooks to update their state
  globalInvalidationCallbacks.forEach(callback => callback());
};

export const useFolderCache = () => {
  const { user } = useAuth();
  const [folderMap, setFolderMap] = useState(globalFolderMap);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(globalLastFetch);
  
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  const isCacheFresh = useCallback(() => {
    return lastFetch && (Date.now() - lastFetch) < CACHE_DURATION;
  }, [lastFetch]);

  // Register this hook for global invalidation
  useEffect(() => {
    const invalidationCallback = () => {
      setFolderMap({});
      setLastFetch(null);
    };
    
    globalInvalidationCallbacks.push(invalidationCallback);
    
    return () => {
      const index = globalInvalidationCallbacks.indexOf(invalidationCallback);
      if (index > -1) {
        globalInvalidationCallbacks.splice(index, 1);
      }
    };
  }, []);

  const loadFolders = useCallback(async (force = false) => {
    if (!user?.userId) return;
    
    // Return cached data if fresh and not forced
    if (!force && isCacheFresh() && Object.keys(globalFolderMap).length > 0) {
      return globalFolderMap;
    }

    try {
      setLoading(true);
      const folders = await api.folders.getAll(user.userId);
      
      // Create folder ID to name mapping
      const newFolderMap = {};
      folders.forEach(folder => {
        newFolderMap[folder.folderId] = folder.name;
      });
      
      // Update global cache
      globalFolderMap = newFolderMap;
      globalLastFetch = Date.now();
      
      // Update local state
      setFolderMap(newFolderMap);
      setLastFetch(globalLastFetch);
      
      return newFolderMap;
    } catch (error) {
      console.error('Error loading folders for cache:', error);
      return globalFolderMap; // Return existing cache on error
    } finally {
      setLoading(false);
    }
  }, [user?.userId, isCacheFresh]);

  // Auto-load on mount and user change
  useEffect(() => {
    if (user?.userId) {
      loadFolders();
    }
  }, [user?.userId]); // Don't include loadFolders to avoid infinite loop

  // Cache invalidation
  const invalidateCache = useCallback(() => {
    invalidateFolderCache();
  }, []);

  // Force refresh
  const refreshCache = useCallback(() => {
    return loadFolders(true);
  }, [loadFolders]);

  // Get folder name by ID
  const getFolderName = useCallback((folderId) => {
    return folderMap[folderId] || null;
  }, [folderMap]);

  return {
    folderMap,
    loading,
    getFolderName,
    loadFolders,
    invalidateCache,
    refreshCache,
    isCacheFresh: isCacheFresh()
  };
};
